// Standard share for rich blocks and saved files (NEXUS-763): the Web Share API with files (navigator.share/canShare),
// falling back to a download. On Android and Windows browsers this opens the system share sheet.

export interface ShareNav {
  share?: (data: { files?: File[]; title?: string; text?: string }) => Promise<void>;
  canShare?: (data: { files?: File[] }) => boolean;
}
export type ShareResult = 'shared' | 'downloaded' | 'cancelled';

export function canShareFiles(nav: ShareNav | undefined, files: File[]): boolean {
  return !!nav && typeof nav.share === 'function' && typeof nav.canShare === 'function' && nav.canShare({ files });
}

/** Share the files through the system sheet, or hand each to `download` when the browser can't share files.
 *  A dismissed sheet (AbortError) is not an error; any other share failure falls back to the download. */
export async function shareOrDownload(nav: ShareNav | undefined, files: File[], title: string, download: (f: File) => void): Promise<ShareResult> {
  if (canShareFiles(nav, files)) {
    try { await nav!.share!({ files, title }); return 'shared'; }
    catch (e) { if ((e as { name?: string } | null)?.name === 'AbortError') return 'cancelled'; }
  }
  for (const f of files) download(f);
  return 'downloaded';
}

export function downloadFile(f: File): void {
  const url = URL.createObjectURL(f);
  const a = document.createElement('a');
  a.href = url; a.download = f.name; a.rel = 'noopener';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const withExt = (name: string, ext: string): string => name.replace(/\.[^.]+$/, '') + '.' + ext;

/** The files a block/saved file shares: text as itself; an SVG (drawn or mermaid) also as a PNG. */
export function textFile(name: string, mime: string, text: string): File {
  return new File([text], name, { type: `${mime};charset=utf-8` });
}

/** Size of a sanitized SVG from its width/height or viewBox (px), defaulting to 800x600. */
export function svgSize(svg: string): { w: number; h: number } {
  const num = (re: RegExp): number | null => { const m = svg.match(re); return m ? parseFloat(m[1]) : null; };
  const head = svg.slice(0, svg.indexOf('>') + 1);
  const w = num(/\swidth="([\d.]+)(?:px)?"/) ?? null;
  const h = num(/\sheight="([\d.]+)(?:px)?"/) ?? null;
  if (w && h && head.includes(`width="${w}`)) return { w, h };
  const vb = head.match(/viewBox="[-\d.]+[ ,]+[-\d.]+[ ,]+([\d.]+)[ ,]+([\d.]+)"/);
  if (vb) return { w: parseFloat(vb[1]), h: parseFloat(vb[2]) };
  return { w: 800, h: 600 };
}

/** Rasterise a sanitized SVG to PNG (2x, white background). Browser only. */
export async function svgToPng(svg: string, name: string): Promise<File> {
  const { w, h } = svgSize(svg);
  const scale = 2;
  const src = svg.includes('xmlns=') ? svg : svg.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
  const img = new Image();
  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(src)}`;
  await new Promise<void>((ok, bad) => { img.onload = () => ok(); img.onerror = () => bad(new Error('could not rasterise the SVG')); img.src = url; });
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w * scale)); canvas.height = Math.max(1, Math.round(h * scale));
  const g = canvas.getContext('2d');
  if (!g) throw new Error('no canvas');
  g.fillStyle = '#ffffff'; g.fillRect(0, 0, canvas.width, canvas.height);
  g.drawImage(img, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, 'image/png'));
  if (!blob) throw new Error('PNG export failed');
  return new File([blob], withExt(name, 'png'), { type: 'image/png' });
}

/** Files to share for an SVG: the .svg and its PNG (PNG is skipped if rasterising fails). */
export async function svgShareFiles(name: string, svg: string): Promise<File[]> {
  const files = [textFile(name, 'image/svg+xml', svg)];
  try { files.push(await svgToPng(svg, name)); } catch { /* svg alone is still shareable */ }
  return files;
}

export async function shareFiles(files: File[], title: string): Promise<ShareResult> {
  return shareOrDownload(typeof navigator === 'undefined' ? undefined : (navigator as unknown as ShareNav), files, title, downloadFile);
}
